/** @jsxImportSource solid-js */
import { Accordion } from "#palette/components/accordion.solid";
import { createCheckboxInput } from "#palette/components/form/checkbox.solid";
import { createPasswordInput } from "#palette/components/form/password.solid";
import { createPhoneInput } from "#palette/components/form/phone.solid";
import { SubmitButton } from "#palette/components/form/submit.solid";
import { createTextInput } from "#palette/components/form/text.solid";
import { globalState } from "#root/frontend/state.js";
import { createSignal } from "solid-js";

/**
 * Show a page for users to enter their basic account information.
 */
export function Step_3_Account(props: {
  goNext: () => void;
  setPhone: (p: string) => void;
  setUsername: (u: string) => void;
  setPassword: (p: string) => void;
  setError: (m: string) => void;
  resetError: () => void;
}) {
  let credentialsForm!: HTMLFormElement;
  const username = createTextInput({
    label: "Username",
    placeholder: "Choose a unique username",
    tooltip: (
      <>
        <p class="py-2">
          We suggest that you choose a username you have not used before and
          that is not identifiable, but that you will remember. For example, if
          your name is Sara Smith, avoid choosing the usernames SaraSmith,
          SSmith, SaraS, SaraSara, or SmithSmith.
        </p>
        <p class="py-2 font-bold">
          Once the account is created, your username cannot be changed.
        </p>
      </>
    ),
  });
  const password = createPasswordInput({});
  const confirmPassword = createPasswordInput({
    label: "",
    placeholder: "Confirm your password",
  });
  const passwordsMatch = () => password.value() === confirmPassword.value();

  // These signals are used for checking the password strength, and allowing the user to bypass.
  const [needsPasswordCheck, setNeedsPasswordCheck] = createSignal(true);
  const [passwordIsWeak, setPasswordIsWeak] = createSignal(false);
  const passwordAcceptRisk = createCheckboxInput(
    "I accept the risks involved in using this password, and wish to use it anyway.",
  );
  const passwordRisky = () => passwordIsWeak() && !passwordAcceptRisk.value();

  // Though this isn't used until the next step, we collect it here because it can be a sticking point for people
  // signing up for the site and we want them to dip out before their account is officially created.
  const phoneNumber = createPhoneInput({
    label: "Enter your phone number",
    tooltip: (
      <>
        <p class="py-2 font-bold">Your phone number is safe with us.</p>
        <p class="py-2">
          We need your phone number to contact you about a matched entry, or to
          recover your account.
        </p>
        <p class="py-2">
          Much like your email address, this phone number is not searchable in
          our database and can only be viewed by an advocate assigned to your
          case.
        </p>
      </>
    ),
  });
  const confirmPhoneNumber = createPhoneInput({
    label: "",
    placeholder: "Confirm your phone number",
  });
  const phonesMatch = () => phoneNumber.value() === confirmPhoneNumber.value();

  const majorityAge = createCheckboxInput("I am at least 18 years of age.");
  const privacyPolicy = createCheckboxInput(
    <>
      I have read the{" "}
      <a
        href="https://www.projectcallisto.org/callisto-vault-privacy-policy"
        target="_blank"
        rel="noopener"
      >
        Callisto Vault Privacy Policy
      </a>{" "}
      and I accept its terms.
    </>,
  );

  async function saveThisPageInfo() {
    setPasswordIsWeak(false);

    if (needsPasswordCheck()) {
      let abort = false;
      await globalState.client
        .checkPasswordStrength(password.value())
        .catch(() => {
          abort = true;
          setPasswordIsWeak(true);
        })
        .finally(() => {
          setNeedsPasswordCheck(false);
        });
      // Quit out early so we can kick the user back to resetting their password.
      if (abort) {
        credentialsForm.setAttribute("data-validate", "true");
        return;
      }
    }

    if (
      username.value() === "" ||
      password.value() === "" ||
      !passwordsMatch() ||
      !phonesMatch() ||
      !majorityAge.value() ||
      !privacyPolicy.value() ||
      passwordRisky()
    ) {
      credentialsForm.setAttribute("data-validate", "true");
      return;
    }

    // We don't actually process anything on this page- everything will be submitted once the recovery information
    // is entered, so we don't have half-created accounts lying around.
    props.setPassword(password.value());
    props.setUsername(username.value());
    props.setPhone(phoneNumber.value());
    props.goNext();
  }

  return (
    <>
      <h1 class="mb-3 mt-6 text-xl font-bold  text-brand-900 font-serif">
        Create Your Account Details
      </h1>

      <form class="group" data-validate="false" ref={credentialsForm}>
        <div class="w-full mt-4">
          {username.component}
          {password.component}
          {confirmPassword.component}
          {!passwordsMatch() && (
            <strong class="text-status-error-fg p-2 hidden group-data-[validate=true]:block">
              Please confirm your password by entering it twice.
            </strong>
          )}
          {passwordIsWeak() && (
            <>
              <strong class="text-status-error-fg p-2 hidden group-data-[validate=true]:block">
                {" "}
                The password you chose may have been compromised on another
                website. (
                <a
                  href="https://haveibeenpwned.com/Passwords"
                  target="_blank"
                  rel="noreferrer"
                >
                  click here for more details
                </a>
                ).
              </strong>
              {passwordAcceptRisk.component}
            </>
          )}
        </div>
        <div class="w-full mt-4">
          {phoneNumber.component}
          {confirmPhoneNumber.component}
          {!phonesMatch() && (
            <strong class="text-status-error-fg p-2 hidden group-data-[validate=true]:block">
              Please confirm your phone number by entering it twice.
            </strong>
          )}
        </div>
        <div class="w-full mt-8">
          {majorityAge.component}
          {privacyPolicy.component}
          {!majorityAge.value() && !privacyPolicy.value() && (
            <strong class="text-status-error-fg p-2 hidden group-data-[validate=true]:block">
              Please confirm that you are the above the age of majority and that
              you accept the privacy policy.
            </strong>
          )}
        </div>
        <SubmitButton
          onClick={saveThisPageInfo}
          disabled={false}
          title={"Continue"}
        />
      </form>
    </>
  );
}
